import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../../auth/contexts/AuthContext";
import {
  onboardingTours,
  routeTourMap,
  defaultTourId,
  type OnboardingStep,
  type OnboardingTourId,
} from "./onboardingSteps";

interface OnboardingContextValue {
  isOpen: boolean;
  steps: OnboardingStep[];
  startTour: () => void;
  stopTour: () => void;
}

const OnboardingContext = createContext<OnboardingContextValue | undefined>(
  undefined,
);

const STORAGE_PREFIX = "crdb_onboarding_done";
const AUTO_START_DELAY_MS = 900;

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const { currentUser } = useAuth();
  const location = useLocation();

  const [isOpen, setIsOpen] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<OnboardingTourId | null>(
    null,
  );
  const autoStartedToursRef = useRef<Set<string>>(new Set());

  // Identifiant stable par utilisateur afin que chaque nouveau compte
  // découvre le guide, sans le re-déclencher pour les utilisateurs existants.
  const userKey = currentUser?.uid || currentUser?.email || "guest";

  const storageKeyFor = useCallback(
    (tourId: string) => `${STORAGE_PREFIX}_${userKey}_${tourId}`,
    [userKey],
  );

  const activeTourId = (routeTourMap[location.pathname] ??
    null) as OnboardingTourId | null;

  const steps = currentTourId ? onboardingTours[currentTourId] : [];

  const startTour = useCallback(() => {
    setCurrentTourId(activeTourId ?? defaultTourId);
    setIsOpen(true);
  }, [activeTourId]);

  const stopTour = useCallback(() => {
    if (currentTourId) {
      localStorage.setItem(storageKeyFor(currentTourId), "true");
    }
    setIsOpen(false);
  }, [currentTourId, storageKeyFor]);

  // Démarrage automatique à la première visite d'une page disposant d'un tour.
  useEffect(() => {
    if (!currentUser) return;
    if (!activeTourId) return;
    if (autoStartedToursRef.current.has(activeTourId)) return;

    if (localStorage.getItem(storageKeyFor(activeTourId)) === "true") {
      autoStartedToursRef.current.add(activeTourId);
      return;
    }

    const timer = setTimeout(() => {
      autoStartedToursRef.current.add(activeTourId);
      setCurrentTourId(activeTourId);
      setIsOpen(true);
    }, AUTO_START_DELAY_MS);

    return () => clearTimeout(timer);
  }, [activeTourId, currentUser, storageKeyFor]);

  // Ferme le tour lorsqu'on navigue vers une autre page.
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  return (
    <OnboardingContext.Provider
      value={{ isOpen, steps, startTour, stopTour }}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const useOnboarding = () => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};
